import { z } from 'zod';

// Attachment metadata schema - new format for message attachments
export const AttachmentMetadataSchema = z.object({
  file_id: z.string().describe('Permanent S3 key for the file'),
  url: z.string().describe('Presigned URL for temporary access'),
  expires_at: z.string().datetime().describe('ISO timestamp when the URL expires'),
  file_name: z.string().optional().describe('Original filename (optional)'),
  file_size_mb: z.number().optional().describe('File size in megabytes'),
  estimated_tokens: z.number().optional().describe('Estimated token count for context tracking'),
  content_type: z.string().optional().describe('MIME type of the file'),
});
export type AttachmentMetadata = z.infer<typeof AttachmentMetadataSchema>;

// Metadata about a captured web page attached to a user message
export const PageContextMetaSchema = z.object({
  title: z.string(),
  url: z.string(),
  charCount: z.number().int(),
  wasTruncated: z.boolean(),
});
export type PageContextMeta = z.infer<typeof PageContextMetaSchema>;

// Chat message schema - individual message in a chat session
export const ChatMessageSchema = z.object({
  content: z.string(),
  // Support both legacy (string S3 keys or URLs) and new attachment metadata format
  attached_files: z
    .array(z.union([z.string(), AttachmentMetadataSchema]))
    .default([])
    .describe('Array of attachment metadata objects (new) or strings (legacy)'),
  created_at: z.string().datetime(), // ISO 8601 datetime string
  sender: z.enum(['user', 'ai']), // Who sent the message
  id: z.string().optional(), // Optional message ID for tracking
  page_context: PageContextMetaSchema.optional().describe(
    'Metadata for a captured web page attached to this message (display only, not sent to AI)'
  ),
});

export type ChatMessage = z.infer<typeof ChatMessageSchema>;

// Chat session metadata - stored in the chat folder
export const ChatSessionMetadataSchema = z.object({
  session_id: z.string(),
  title: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  message_count: z.number().int().min(0),
  total_files: z.number().int().min(0).default(0), // Total attached files in this chat
  is_archived: z.boolean().default(false), // Whether the chat is archived
  // Context compaction — set when the user triggers context shrink via the OpenAI Compact API
  compaction_token: z.string().optional(),
  compaction_message_index: z.number().int().min(0).optional(),
  // AI Specialist — persisted so reopening a chat restores the active skill
  ai_skill_id: z.string().optional(),
});

export type ChatSessionMetadata = z.infer<typeof ChatSessionMetadataSchema>;

// Complete chat session - includes metadata and all messages
export const ChatSessionSchema = z.object({
  metadata: ChatSessionMetadataSchema,
  messages: z.array(ChatMessageSchema),
});

export type ChatSession = z.infer<typeof ChatSessionSchema>;

// Chat index entry schema - used in user's index.json
export const ChatIndexEntrySchema = z.object({
  session_id: z.string(),
  title: z.string(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  message_count: z.number().int().min(0),
  is_archived: z.boolean().default(false), // Whether the chat is archived
});

export type ChatIndexEntry = z.infer<typeof ChatIndexEntrySchema>;

// User chat history index - stored in user's root folder
export const UserChatHistoryIndexSchema = z.object({
  user_id: z.string(),
  chats: z.array(ChatIndexEntrySchema),
  last_updated: z.string().datetime(),
});

export type UserChatHistoryIndex = z.infer<typeof UserChatHistoryIndexSchema>;

// API request/response schemas
export const CreateChatSessionInputSchema = z.object({
  title: z.string().optional(),
});

export const CreateChatSessionOutputSchema = z.object({
  session_id: z.string(),
  title: z.string(),
  created_at: z.string().datetime(),
});

export const SaveChatMessageInputSchema = z.object({
  session_id: z.string(),
  message: ChatMessageSchema,
});

export const SaveChatMessageOutputSchema = z.object({
  success: z.boolean(),
  message_id: z.string().optional(),
});

export const GetChatHistoryOutputSchema = z.object({
  session: ChatSessionSchema,
});

export const GetUserChatsOutputSchema = z.object({
  chats: z.array(ChatIndexEntrySchema),
});

export const DeleteChatInputSchema = z.object({
  session_id: z.string(),
});

export const DeleteChatOutputSchema = z.object({
  success: z.boolean(),
});

// Archive/Unarchive schemas
export const ArchiveChatOutputSchema = z.object({
  success: z.boolean(),
  is_archived: z.boolean(),
});

export const UpdateChatTitleInputSchema = z.object({
  title: z.string().min(1).max(200),
});

export const UpdateChatTitleOutputSchema = z.object({
  success: z.boolean(),
  title: z.string(),
});

// File upload for chat context
export const ChatFileUploadInputSchema = z.object({
  session_id: z.string(),
  file_name: z.string(),
  file_content: z.string(), // Base64 encoded file content
  mime_type: z.string(),
});

export const ChatFileUploadOutputSchema = z.object({
  file_key: z.string(), // S3 key where file was stored
  file_url: z.string().optional(), // Optional pre-signed URL for access
  expires_at: z.string().optional(), // When the presigned URL expires
});

export type CreateChatSessionInput = z.infer<typeof CreateChatSessionInputSchema>;
export type CreateChatSessionOutput = z.infer<typeof CreateChatSessionOutputSchema>;
export type SaveChatMessageInput = z.infer<typeof SaveChatMessageInputSchema>;
export type SaveChatMessageOutput = z.infer<typeof SaveChatMessageOutputSchema>;
export type GetChatHistoryOutput = z.infer<typeof GetChatHistoryOutputSchema>;
export type GetUserChatsOutput = z.infer<typeof GetUserChatsOutputSchema>;
export type DeleteChatInput = z.infer<typeof DeleteChatInputSchema>;
export type DeleteChatOutput = z.infer<typeof DeleteChatOutputSchema>;
export type ArchiveChatOutput = z.infer<typeof ArchiveChatOutputSchema>;
export type UpdateChatTitleInput = z.infer<typeof UpdateChatTitleInputSchema>;
export type UpdateChatTitleOutput = z.infer<typeof UpdateChatTitleOutputSchema>;
export type ChatFileUploadInput = z.infer<typeof ChatFileUploadInputSchema>;
export type ChatFileUploadOutput = z.infer<typeof ChatFileUploadOutputSchema>;

// JSON Export/Import schemas
export const JSONExportInputSchema = z.object({
  session_id: z.string().optional(), // If provided, export single session; if not, export all
  file_name: z.string().optional(), // Custom filename
});

export const JSONExportOutputSchema = z.object({
  s3_key: z.string(), // S3 key where JSON was saved
  download_url: z.string(), // Presigned URL for download
  file_name: z.string(), // Actual filename used
  expires_at: z.string(), // When the download URL expires
});

export const JSONImportInputSchema = z.object({
  json_content: z.string(), // Base64 encoded JSON content OR raw JSON string
  overwrite_existing: z.boolean().default(false),
});

export const JSONImportOutputSchema = z.object({
  success: z.boolean(),
  sessions_imported: z.number(),
  sessions_skipped: z.number(),
  message: z.string(),
});

export const ListJSONExportsOutputSchema = z.object({
  exports: z.array(
    z.object({
      file_name: z.string(),
      created_at: z.string(),
      size_bytes: z.number().optional(),
    })
  ),
});

export type JSONExportInput = z.infer<typeof JSONExportInputSchema>;
export type JSONExportOutput = z.infer<typeof JSONExportOutputSchema>;
export type JSONImportInput = z.infer<typeof JSONImportInputSchema>;
export type JSONImportOutput = z.infer<typeof JSONImportOutputSchema>;
export type ListJSONExportsOutput = z.infer<typeof ListJSONExportsOutputSchema>;
